import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { getChatSocket } from '@/lib/chat';

/**
 * The conversation list, and the unread total the header badge reads.
 *
 * Conversations are read STRAIGHT FROM SUPABASE under RLS, not through the
 * socket. Migration 0022 grants SELECT to participants and nobody else, so the
 * database is already the authority — routing a list through the socket would
 * add a round trip and a second place for the rule to be wrong.
 *
 * The socket is only used for what it is good at: telling us something
 * changed.
 *
 * A module-level store, like lib/data/wallet.ts, because the badge in the
 * header and the messages page both need this and must not disagree.
 */

export interface ConversationSummary {
  id: string;
  otherId: string;
  otherName: string;
  otherAvatarUrl: string | null;
  lastMessageAt: string | null;
  unread: number;
}

interface Store {
  conversations: ConversationSummary[];
  loading: boolean;
  error: string | null;
  userId: string | null;
}

let store: Store = { conversations: [], loading: true, error: null, userId: null };
const listeners = new Set<() => void>();

const setStore = (next: Partial<Store>) => {
  store = { ...store, ...next };
  for (const l of listeners) l();
};

let inflight: Promise<void> | null = null;

interface Row {
  id: string;
  user_id: string;
  companion_id: string;
  last_message_at: string | null;
}

export function refreshConversations(userId: string | null): Promise<void> {
  if (!userId) {
    setStore({ conversations: [], loading: false, userId: null });
    return Promise.resolve();
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('id, user_id, companion_id, last_message_at')
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw new Error(error.message);

      const rows = (data ?? []) as unknown as Row[];
      const otherIds = rows.map((r) => (r.user_id === userId ? r.companion_id : r.user_id));

      /*
       * Names in one query rather than one per row. An embedded join is not
       * available here — `conversations` has two foreign keys into `profiles`
       * and PostgREST refuses an ambiguous embed, the same problem
       * routes/admin.ts documents for companion_profiles.
       */
      const { data: people } = otherIds.length
        ? await supabase.from('profiles').select('id, display_name, avatar_url').in('id', otherIds)
        : { data: [] };

      const byId = new Map(
        (people ?? []).map((p) => [
          p.id as string,
          { name: (p.display_name as string | null) ?? 'Someone', avatar: p.avatar_url as string | null },
        ]),
      );

      /*
       * Unread counts. Selecting only the ids of unread messages and counting
       * client-side, because PostgREST cannot group — and the partial index in
       * 0022 (`where read_at is null`) makes it cheap regardless of how long
       * the history is.
       */
      const { data: unreadRows } = await supabase
        .from('messages')
        .select('conversation_id')
        .is('read_at', null)
        .neq('sender_id', userId);

      const unread = new Map<string, number>();
      for (const m of (unreadRows ?? []) as { conversation_id: string }[]) {
        unread.set(m.conversation_id, (unread.get(m.conversation_id) ?? 0) + 1);
      }

      const conversations: ConversationSummary[] = rows.map((r) => {
        const otherId = r.user_id === userId ? r.companion_id : r.user_id;
        const person = byId.get(otherId);
        return {
          id: r.id,
          otherId,
          otherName: person?.name ?? 'Someone',
          otherAvatarUrl: person?.avatar ?? null,
          lastMessageAt: r.last_message_at,
          unread: unread.get(r.id) ?? 0,
        };
      });

      setStore({ conversations, error: null, loading: false, userId });
    } catch (err) {
      setStore({
        error: err instanceof Error ? err.message : 'Could not load your messages.',
        loading: false,
      });
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Adjust one thread's unread count without refetching the whole list. */
export function setUnread(conversationId: string, unread: number): void {
  setStore({
    conversations: store.conversations.map((c) =>
      c.id === conversationId ? { ...c, unread } : c,
    ),
  });
}

export function useConversations() {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;

  const snapshot = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => store,
    () => store,
  );

  useEffect(() => {
    if (!userId) return;
    if (snapshot.userId !== userId) void refreshConversations(userId);
  }, [userId, snapshot.userId]);

  /*
   * One subscription for the whole app, attached here rather than in a
   * component, so the badge updates whether or not the messages page is open.
   */
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let detach: (() => void) | undefined;

    void (async () => {
      const s = await getChatSocket();
      if (!s || cancelled) return;

      const onNotification = (p: { conversationId: string; unread: number }) => {
        // The server already counted; trust it rather than refetching the
        // whole list for every message.
        setUnread(p.conversationId, p.unread);
        setStore({
          conversations: store.conversations.map((c) =>
            c.id === p.conversationId ? { ...c, lastMessageAt: new Date().toISOString() } : c,
          ),
        });
      };

      // A message can arrive for a conversation the list has never seen — the
      // first message on a brand new thread.
      const onNew = () => void refreshConversations(userId);

      s.on('message_notification', onNotification);
      s.on('message_new', onNew);
      detach = () => {
        s.off('message_notification', onNotification);
        s.off('message_new', onNew);
      };

      // The effect may have been torn down while getChatSocket was awaiting.
      if (cancelled) detach();
    })();

    return () => {
      cancelled = true;
      // Only our own handlers. removeAllListeners would take out whichever
      // other component happened to be listening to the same socket.
      detach?.();
    };
  }, [userId]);

  const refresh = useCallback(() => refreshConversations(userId), [userId]);

  return {
    conversations: snapshot.userId === userId ? snapshot.conversations : [],
    loading: snapshot.loading,
    error: snapshot.error,
    refresh,
  };
}

/** Total unread across every thread — what the header badge shows. */
export function useUnreadTotal(): number {
  const { conversations } = useConversations();
  return conversations.reduce((sum, c) => sum + c.unread, 0);
}

/**
 * The thread with a particular person, if one exists.
 *
 * A client cannot create one — `conversations` has no INSERT policy. The row
 * is made by a trigger the moment a booking between the two reaches
 * `confirmed` (migration 0023), so an empty list before that is the correct
 * state rather than something to fix.
 */
export const conversationWith = (otherId: string): ConversationSummary | null =>
  store.conversations.find((c) => c.otherId === otherId) ?? null;

/** Relative time for a list row: "2m", "3h", "Tue". */
export function shortWhen(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60_000);

  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h`;
  if (mins < 60 * 24 * 7) {
    return new Date(iso).toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
